"use client";
import { useCartModal } from "states/modals";
import { useMultipleProducts } from "context/cart";
import { useRouter } from "next/navigation";
import { useState, Suspense, useEffect } from "react";
import { FiShare2 } from "react-icons/fi";
import { GrAdd, GrSubtract } from "react-icons/gr";
import styles from "./page.module.css";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { basicFetcher } from "utils/fetcher";
import Link from "next/link";
import Image from "next/image";
import useUser from "hooks/useUser";
import { useLikes } from "hooks/useLikes";
import { BsSuitHeart, BsSuitHeartFill } from "react-icons/bs";
import axios from "axios";
import dynamic from "next/dynamic";
import { addProduct, getRecent } from "utils/history";
import { useCartStore } from "states/cart";
import { payments } from "app/checkout/variables";

const Modal = dynamic(() => import("./clio").then(({ Modal }) => Modal)) as any;
const ShareContent = dynamic(() =>
  import("./clio").then(({ ShareContent }) => ShareContent)
) as any;

export function Recommended({
  price,
  category,
  id,
}: {
  price: number;
  id: string;
  category: string;
}) {
  const {
    data = {
      results: [],
    },
  } = useSWR<Paginated<Product>>(
    `products/?${new URLSearchParams({
      min_price: (price * 0.7).toString(),
      max_price: (price * 1.3).toString(),
      category,
      limit: "10",
    })}`,
    basicFetcher
  );

  return (
    <RenderProducts
      results={data.results.filter((product) => id !== product.id)}
    />
  );
}

export function Recent({ id }: { id: string }) {
  const { data = [] } = useMultipleProducts(getRecent());

  const recent = getRecent();

  data.sort((a, b) => {
    const indexA = recent.indexOf(a.id);
    const indexB = recent.indexOf(b.id);
    return indexA - indexB;
  });

  return (
    <RenderProducts results={data.filter((product) => id !== product.id)} />
  );
}

function RenderProducts({ results }: { results: Product[] }) {
  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <ul className={styles.products}>
        {results.map((product) => (
          <li key={product.id}>
            <Link href={`/product/${product.id}`}>
              <div>
                <Image src={product.primary_img_url} fill sizes="(max-width: 768px) 100vw, 400px" alt="" />
              </div>
              <h3>{product.name}</h3>
              <p>
                &#x20A6;
                {(
                  product.price + (product.installation_fee || 0)
                ).toLocaleString()}
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SaveProduct({ id }: { id: string }) {
  const { data: user } = useUser();
  const {
    data: likes = {
      data: [],
    },
    isLoading,
    mutate,
  } = useLikes();

  const liked = likes.data.some((like) => like.id === id);

  const like = useSWRMutation(
    `/users/favourites/${id}`,
    async (key) => {
      await axios(key, {
        method: liked ? "DELETE" : "PUT",
      });
    },
    {
      onSuccess() {
        mutate();
      },
    }
  );

  if (!user) return null;
  return (
    <button
      disabled={isLoading || like.isMutating}
      onClick={() => {
        like.trigger();
      }}
    >
      {liked ? <BsSuitHeartFill /> : <BsSuitHeart />}
    </button>
  );
}

export function AddProductToRecent({ id }: { id: string }) {
  useEffect(() => {
    addProduct(id);
  }, [id]);

  return null;
}

export function AddToCartForm({
  id,
  max,
  category,
}: {
  id: string;
  max: number;
  category: string;
}) {
  const { cart, changeQty } = useCartStore();
  const router = useRouter();
  const { setOpen } = useCartModal();
  const [tbA, setTbA] = useState(() => cart[id] || 1);
  const isComplete = category === "Complete Solution";
  const isSolarHomeSystem = category === "Solar All-In-One Box";
  const [sharing, setSharing] = useState(false);
  const [lto, setLTO] = useState(false);
  return (
    <form
      className={styles.cartForm}
      id={"p" + id}
      onSubmit={(e) => {
        e.preventDefault();
        if (isComplete) {
          router.push(`/checkout/${id}`);
        } else {
          changeQty(id, tbA);
          setOpen(true);
        }
      }}
    >
      <span>In stock</span>
      <div className="">
        <div
          style={{
            display: isComplete ? "none" : undefined,
          }}
          className={styles.control}
        >
          <button
            type="button"
            title="Subtract"
            onClick={() => {
              setTbA((tbA) => (tbA < 1 ? tbA : tbA - 1));
            }}
          >
            <GrSubtract />
          </button>
          <input
            type="number"
            min={1}
            max={max}
            value={tbA}
            onChange={(e) => {
              const value = e.target.valueAsNumber;
              setTbA(isNaN(value) || value < 1 ? 1 : value);
            }}
          />
          <button
            type="button"
            title="Add"
            onClick={() => {
              setTbA((tbA) => (tbA >= max ? tbA : tbA + 1));
            }}
          >
            <GrAdd />
          </button>
        </div>
        <button
          className="btn"
          type="button"
          onClick={() => {
            const toBeShared = {
              url: location.href,
              title: "Buy on Imperium",
              text: `Check out this ${category.toLowerCase()} on Imperium`,
            };
            if (
              "canShare" in navigator &&
              "share" in navigator &&
              navigator.canShare(toBeShared)
            ) {
              navigator.share(toBeShared);
            } else {
              setSharing(true);
            }
          }}
        >
          <FiShare2 />
          Share
        </button>
      </div>
      <button className="btn btn-lg btn-green">
        {isComplete ? "Proceed to checkout" : "Add to Cart"}
      </button>
      {isComplete && (
        <>
        <hr />
          <p>
            Strapped for cash? try our financing option
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setLTO(true);
              }}
            >
              Learn More
            </a>
            <Suspense>
              <Modal open={lto} onClose={setLTO} title={payments.lease.name}>
                {payments.lease.explainer}
              </Modal>
            </Suspense>
          </p>
          <hr />
        </>
      )}
      <Suspense>
        <Modal
          open={sharing}
          onClose={setSharing}
          title="Share on your favorite social networks"
        >
          <ShareContent close={setSharing} />
        </Modal>
      </Suspense>
    </form>
  );
}

export function SelectLocation({
  locations,
  ...others
}: { locations: Product["locations_list"] } & DeFee) {
  const [state, setState] = useState(
    () => locations.find((loc) => loc.name === "Lagos") || locations[0]
  );

  return (
    <div className={styles.selloc}>
      <div className={styles.dee}>
        <label htmlFor="sel-loc">Select your location</label>
      </div>
      <select
        value={state.name}
        onChange={(event) => {
          setState(
            locations.find((loc) => loc.name === event.target.value) ||
              locations[0]
          );
        }}
        name=""
        id="sel-loc"
      >
        {locations.map((loc) => (
          <option key={loc.name} value={loc.name}>
            {loc.name}
          </option>
        ))}
      </select>
      <small>
        + Aprox shipping cost from ₦
        {others[`${state.region}_delivery_fee`].toLocaleString()} to{" "}
        {state.name}
      </small>
      <div className={styles.dee}>
        <p>
          Average shipping & delivery times on most products is{" "}
          {state.name === "Lagos" ? "1 - 3 " : "1 - 7 "} business days
        </p>
      </div>
    </div>
  );
}

export function PayWithSpectaModal() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <span className="!no-underline">Loan Available</span>{" "}
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          setOpen(true);
        }}
      >
        Learn More
      </a>
      <Suspense>
        <Modal open={open} onClose={setOpen} title={payments.lease.name}>
          {payments.lease.explainer}
        </Modal>
      </Suspense>
    </>
  );
}
